// Discharge dialog — Phase 3 (2026-10-03).
//
// Doctor/admin-only. Nurses can cancel (mis-tap recovery) but cannot
// discharge — that's a clinical decision. Disposition is a radio: 'home',
// 'escalated_to_hospital', 'left_against_advice', 'deceased'. Note is
// optional free text that joins the audit trail.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdmissionDisposition, DischargeRequest } from '@pamosi/shared';
import { ADMISSION_DISPOSITION_LABELS } from '@pamosi/shared';
import { ApiError, admissionsApi } from '../lib/api';
import { ModalShell } from './AdmitDialog';

interface Props {
    admissionId: string;
    encounterId: string | null;
    onClose: () => void;
    onDischarged: () => void;
}

const OPTIONS: AdmissionDisposition[] = [
    'home',
    'escalated_to_hospital',
    'left_against_advice',
    'deceased',
];

export function DischargeDialog({ admissionId, encounterId, onClose, onDischarged }: Props) {
    const qc = useQueryClient();
    const [disposition, setDisposition] = useState<AdmissionDisposition>('home');
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const mut = useMutation({
        mutationFn: (body: DischargeRequest) => admissionsApi.discharge(admissionId, body),
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admissions'] }),
                encounterId
                    ? qc.invalidateQueries({ queryKey: ['encounter', encounterId] })
                    : Promise.resolve(),
            ]);
            setError(null);
            onDischarged();
        },
        onError: (err) => {
            setError(err instanceof ApiError ? err.message : 'Discharge failed.');
        },
    });

    function submit() {
        setError(null);
        mut.mutate({
            disposition,
            note: note.trim() === '' ? null : note.trim(),
        });
    }

    return (
        <ModalShell title="Discharge from observation" onClose={onClose}>
            <div className="space-y-3">
                <div>
                    <div className="pm-label mb-1">Disposition</div>
                    <div className="space-y-1">
                        {OPTIONS.map((d) => (
                            <label key={d} className="flex items-center gap-2 text-sm text-slate-800">
                                <input
                                    type="radio"
                                    name="disposition"
                                    value={d}
                                    checked={disposition === d}
                                    onChange={() => setDisposition(d)}
                                    disabled={mut.isPending}
                                />
                                {ADMISSION_DISPOSITION_LABELS[d]}
                            </label>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="pm-label">Note (optional)</label>
                    <textarea
                        className="pm-input min-h-20"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. BP settled to 135/85, patient stable."
                        maxLength={2000}
                        disabled={mut.isPending}
                    />
                </div>
                {error && <div className="pm-error">{error}</div>}
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button
                    type="button"
                    className="pm-btn-ghost"
                    onClick={onClose}
                    disabled={mut.isPending}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="pm-btn-primary"
                    onClick={submit}
                    disabled={mut.isPending}
                >
                    {mut.isPending ? 'Discharging…' : 'Discharge'}
                </button>
            </div>
        </ModalShell>
    );
}
